/**
 * useBucketLines (HOF-19 AC 10/11) — the source lines behind ONE chart cell,
 * fetched only when a reader asks for them.
 *
 * ── The one rule this hook exists to keep ────────────────────────────────────
 *
 * **On demand means on demand.** `enabled` gates the query on a row actually
 * being open, so a rendered panel issues nothing and a chart that draws 700
 * buckets issues nothing. That is not tidiness: AC 10 is stated as a failure
 * condition — *a build that pages all lines for the drawn window fails* — and it
 * has a measured history. Before #2646 the Preisspiegel read one row per priced
 * invoice LINE, 10 pages × 1000, against a tenant holding 33.000 price
 * observations with Erdbeeren alone at ~19–20k. It dropped roughly half the
 * item's history and the chart looked complete. Cell grain moved the payload
 * from "how much did this farm sell" to "how many days did it sell on"; this
 * hook must not undo that by being called eagerly.
 *
 * The server holds the other half of the rule: `item`, `series_id` and `date`
 * are all REQUIRED there, so there is no request that means "every line in the
 * window" even if a future caller wanted one.
 *
 * ── Why the key carries all four parameters ──────────────────────────────────
 *
 * `(item, seriesId, date, bucket)` IS the cell. Two rows of one panel are two
 * different series or two different days, so they are two different cells and
 * two different cache entries — the same reasoning `ownPriceKeys.cellsForItems`
 * spells out at the read above this one, where a superset answering a subset
 * would put one entry behind several scopes with a `data_quality` that describes
 * only one of them.
 *
 * `bucket` is in the key because a month cell and the first day of that month
 * are different questions with the same `date`: the month asks for up to 31
 * days of lines. Left out, a reader switching `?g=` from 1T to 1M would be
 * served the day's lines under the month's heading.
 */
import { useQuery } from '@tanstack/react-query';
import { twinService, type PreisspiegelLinesResponse } from '../services/twinService';

/**
 * The server's two supported drill-down grains.
 *
 * ⚠️ **The Preisspiegel panel always asks for `day`, and there was once a helper
 * here that mapped the CHART's grain onto this instead.** It was wrong (codex
 * round 2, P1): a panel row is one observation booked on one DAY at every chart
 * grain, so asking for `month` expanded that day into its whole month and listed
 * every line the series traded — documents and prices misattributed to the row
 * the reader had pointed at. `month` is for a caller drilling a whole month
 * CELL. If one ever appears, it passes the month's own bucket start, not a row's
 * date.
 */
export type LineBucket = 'day' | 'month';

export const bucketLineKeys = {
  all: ['preisspiegel', 'bucket-lines'] as const,
  cell: (item: string, seriesId: string, date: string, bucket: LineBucket) =>
    [...bucketLineKeys.all, item, seriesId, date, bucket] as const,
};

export interface BucketLinesResult {
  data: PreisspiegelLinesResponse | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * The lines behind one cell.
 *
 * `enabled: false` until the caller opens the row — see the header. Failure is
 * surfaced rather than swallowed: an empty list and a failed read look identical
 * on screen otherwise, which is the #2573 lesson ("a farm with 20.000 price
 * observations was shown the same screen as a farm with none") at the finest
 * grain the app has.
 */
export function useBucketLines(
  params: {
    item: string;
    seriesId: string | undefined;
    date: string;
    bucket: LineBucket;
  },
  enabled: boolean,
): BucketLinesResult {
  const { item, seriesId, date, bucket } = params;
  const query = useQuery({
    queryKey: bucketLineKeys.cell(item, seriesId ?? '', date, bucket),
    queryFn: () =>
      twinService.getPreisspiegelLines({
        item,
        seriesId: seriesId as string,
        date,
        bucket,
      }),
    // Both halves are required: `enabled` is the reader's intent, `seriesId` is
    // whether the request can be spelled at all. A line-grain observation
    // (`ownPriceAdapter`, retained as the #2649 equivalence proof) carries no
    // series, and firing a request with an empty one would ask the server for a
    // cell that cannot exist.
    enabled: enabled && Boolean(seriesId),
    // A cell's lines are immutable for as long as the page is open — the twin
    // changes only on a reprocess — so a reopened row must not re-fetch.
    staleTime: 5 * 60 * 1000,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
